import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FullPlaylist, FullPlaylistItem } from '../../Model/FullPlaylistModel';

const API_BASE = process.env.REACT_APP_API_URL ?? '';
const POLL_MS = 30_000;
const FADE_MS = 600;

type Phase = 'loading' | 'playing' | 'error' | 'not_found';
type Slot = { item: FullPlaylistItem; id: number } | null;

/*
 * Regras de avanço:
 *  - VIDEO  → avança quando o vídeo termina naturalmente (onEnded)
 *             playlist de 1 único vídeo: usa loop nativo, nunca avança
 *  - IMAGEM / TABELA → avança após item.seconds (timer)
 *
 * Dois slots (A / B) com crossfade real:
 *   front (visível) opacity 1 → 0  /  back (oculto) opacity 0 → 1  simultaneamente.
 *   Após FADE_MS: troca papéis SEM mudar opacity.
 */
const PlayerPage = () => {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();

  const [phase, setPhase]   = useState<Phase>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  const [contentA, setContentA] = useState<Slot>(null);
  const [contentB, setContentB] = useState<Slot>(null);
  const [opA, setOpA] = useState(1);
  const [opB, setOpB] = useState(0);
  const [frontSlot, setFrontSlot] = useState<'A' | 'B'>('A');

  const [searchParams]  = useSearchParams();
  const side = (searchParams.get('side') ?? '') as '' | 'left' | 'right';

  const frontRef        = useRef<'A' | 'B'>('A');
  const playlistRef     = useRef<FullPlaylist | null>(null);
  const currentIndexRef = useRef(0);
  const idRef           = useRef(0);
  const timerRef        = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollRef         = useRef<ReturnType<typeof setInterval> | null>(null);
  const fadeLockRef     = useRef(false);
  const playlistDirtyRef = useRef(false);
  const pendingFadeRef  = useRef<(() => void) | null>(null);
  // Refs dos elementos <video> de cada slot (front é o visível)
  const videoEls        = useRef<{ A: HTMLVideoElement | null; B: HTMLVideoElement | null }>({ A: null, B: null });
  const setVideoA = useCallback((el: HTMLVideoElement | null) => { videoEls.current.A = el; }, []);
  const setVideoB = useCallback((el: HTMLVideoElement | null) => { videoEls.current.B = el; }, []);
  const getFrontVideo = () => videoEls.current[frontRef.current];

  // Recarrega o vídeo visível a partir do servidor (recupera de queda de rede / stall)
  const reloadFrontVideo = useCallback(() => {
    const v = getFrontVideo();
    if (!v) return;
    try { v.load(); const p = v.play(); if (p) p.catch(() => {}); } catch { /* ignore */ }
  }, []);

  // ── Clock sync para modo video wall ───────────────────────
  // Corrige drift entre as duas TVs a cada 2s usando o relógio do sistema (NTP)
  useEffect(() => {
    if (!side || phase !== 'playing') return;
    const interval = setInterval(() => {
      const vid = getFrontVideo();
      if (!vid || !vid.duration || !isFinite(vid.duration)) return;
      const expected = (Date.now() / 1000) % vid.duration;
      if (Math.abs(vid.currentTime - expected) > 0.5) vid.currentTime = expected;
    }, 2000);
    return () => clearInterval(interval);
  }, [side, phase]);

  // ── helpers ────────────────────────────────────────────────
  const isSingleVideo = () =>
    (playlistRef.current?.items.length ?? 0) === 1 &&
    playlistRef.current?.items[0]?.type === 'video';

  const fetchPlaylist = useCallback(async (): Promise<FullPlaylist | null> => {
    const res = await fetch(`${API_BASE}/playlist/findByCode/${code}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [code]);

  // Chamado quando o slot de trás sinaliza que está pronto para exibir
  const handleBackReady = useCallback(() => {
    if (pendingFadeRef.current) {
      pendingFadeRef.current();
      pendingFadeRef.current = null;
    }
  }, []);

  // ── crossfade para o próximo índice ────────────────────────
  const advanceItem = useCallback(() => {
    if (!playlistRef.current || !playlistRef.current.items.length || fadeLockRef.current) return;
    fadeLockRef.current = true;

    const nextIndex = (currentIndexRef.current + 1) % playlistRef.current.items.length;
    const nextItem  = playlistRef.current.items[nextIndex];
    const newId     = ++idRef.current;
    const front     = frontRef.current;
    const back: 'A' | 'B' = front === 'A' ? 'B' : 'A';

    // 1. Carrega próximo item no slot de trás (opacity=0 → invisível)
    if (back === 'A') setContentA({ item: nextItem, id: newId });
    else              setContentB({ item: nextItem, id: newId });

    // 2. Função que executa o crossfade (chamada quando back está pronto)
    const startFade = () => {
      if (front === 'A') { setOpA(0); setOpB(1); }
      else               { setOpB(0); setOpA(1); }

      setTimeout(() => {
        frontRef.current = back;
        setFrontSlot(back);
        // A playlist pode ter sido atualizada (poll) durante o fade e encolhido —
        // recalcula o índice contra o tamanho atual para nunca ficar fora do range.
        const len = Math.max(playlistRef.current?.items.length ?? 1, 1);
        currentIndexRef.current = nextIndex % len;
        fadeLockRef.current = false;
        if (front === 'A') setContentA(null);
        else               setContentB(null);
        // Se a playlist mudou durante este fade, dispara novo avanço para exibir o conteúdo atualizado
        if (playlistDirtyRef.current) {
          playlistDirtyRef.current = false;
          advanceItem();
        }
      }, FADE_MS + 50);
    };

    if (nextItem.type === 'video') {
      // Vídeo: aguarda canplay antes de iniciar o fade (TV demora ~1s para decodificar)
      // Fallback de 4s para não travar caso o evento não dispare
      const fallback = setTimeout(() => {
        pendingFadeRef.current = null;
        startFade();
      }, 4000);

      pendingFadeRef.current = () => {
        clearTimeout(fallback);
        startFade();
      };
    } else {
      // Imagem / tabela: 2 frames são suficientes
      requestAnimationFrame(() => requestAnimationFrame(startFade));
    }
  }, []);

  // ── timer para imagens e tabelas ───────────────────────────
  // Vídeos avançam por onEnded — não usam timer
  const scheduleTimer = useCallback((item: FullPlaylistItem, onDone: () => void) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (item.type === 'video') return;
    timerRef.current = setTimeout(onDone, Math.max(3, item.seconds) * 1000);
  }, []);

  // Aplica uma playlist atualizada (poll / volta de conexão).
  // Se um fade já estiver em andamento, marca como "dirty" — o próprio fade,
  // ao terminar, recalcula o índice e dispara o próximo avanço (evita índice
  // fora do range quando itens são removidos e o player travar para sempre).
  const applyPlaylistUpdate = useCallback((updated: FullPlaylist) => {
    if (JSON.stringify(updated) === JSON.stringify(playlistRef.current)) return;
    playlistRef.current = updated;
    if (fadeLockRef.current) {
      playlistDirtyRef.current = true;
      return;
    }
    currentIndexRef.current = -1;
    advanceItem();
  }, [advanceItem]);

  // Re-agenda timer sempre que o slot da frente muda
  useEffect(() => {
    if (phase !== 'playing' || !playlistRef.current) return;
    const items = playlistRef.current.items;
    if (!items.length) return;
    // Defesa extra: se o índice ficou fora do range (playlist encolheu), volta pro início
    if (currentIndexRef.current < 0 || currentIndexRef.current >= items.length) {
      currentIndexRef.current = 0;
    }
    const item = items[currentIndexRef.current];
    if (item) scheduleTimer(item, advanceItem);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [phase, frontSlot, scheduleTimer, advanceItem]);

  // ── carga inicial ──────────────────────────────────────────
  useEffect(() => {
    if (!/^\d{5}$/.test(code)) { navigate('/tv', { replace: true }); return; }
    fetchPlaylist()
      .then(data => {
        if (!data) { setPhase('not_found'); return; }
        playlistRef.current = data;
        currentIndexRef.current = 0;
        setContentA({ item: data.items[0], id: ++idRef.current });
        frontRef.current = 'A';
        setFrontSlot('A');
        setOpA(1); setOpB(0);
        setPhase('playing');
      })
      .catch(() => { setErrorMsg('Servidor indisponível. Tentando reconectar...'); setPhase('error'); });
  }, [code, fetchPlaylist, navigate]);

  // ── poll de atualização ────────────────────────────────────
  useEffect(() => {
    if (phase !== 'playing') return;
    pollRef.current = setInterval(async () => {
      try {
        const updated = await fetchPlaylist();
        if (!updated) return;
        applyPlaylistUpdate(updated);
      } catch { /* retry on next poll */ }
    }, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [phase, fetchPlaylist, applyPlaylistUpdate]);

  // ── retry automático ───────────────────────────────────────
  useEffect(() => {
    if (phase !== 'error') return;
    const t = setTimeout(async () => {
      try {
        const data = await fetchPlaylist();
        if (!data) { setPhase('not_found'); return; }
        playlistRef.current = data;
        currentIndexRef.current = 0;
        setContentA({ item: data.items[0], id: ++idRef.current });
        frontRef.current = 'A';
        setFrontSlot('A');
        setOpA(1); setOpB(0);
        setPhase('playing');
      } catch { setErrorMsg('Servidor indisponível. Tentando reconectar...'); }
    }, 10_000);
    return () => clearTimeout(t);
  }, [phase, fetchPlaylist]);

  // ── watchdog anti-travamento ───────────────────────────────
  // Vídeo só avança por onEnded; se a rede cai no meio do buffer ele congela
  // e nunca dispara onEnded → trava para sempre. Aqui detectamos que o vídeo da
  // frente parou de progredir e forçamos um reload (que se recupera ao voltar a net).
  useEffect(() => {
    if (phase !== 'playing') return;
    const STALL_MS = 8_000;
    let lastIndex = currentIndexRef.current;
    let lastTime  = 0;
    let lastTs    = Date.now();

    const id = setInterval(() => {
      const now = Date.now();
      // Troca de item = progresso (vale para imagem/tabela via timer)
      if (currentIndexRef.current !== lastIndex) {
        lastIndex = currentIndexRef.current; lastTime = 0; lastTs = now; return;
      }
      const item = playlistRef.current?.items[currentIndexRef.current];
      if (item?.type !== 'video') { lastTs = now; return; }

      const v = getFrontVideo();
      if (!v) return;
      // Qualquer mudança no currentTime (inclusive ao dar loop) conta como progresso
      if (Math.abs(v.currentTime - lastTime) > 0.05) {
        lastTime = v.currentTime; lastTs = now; return;
      }
      // Sem progresso por STALL_MS → vídeo congelado (provável queda de rede)
      if (now - lastTs > STALL_MS) { reloadFrontVideo(); lastTs = now; }
    }, 4_000);
    return () => clearInterval(id);
  }, [phase, reloadFrontVideo]);

  // ── recuperação imediata ao voltar a internet ──────────────
  useEffect(() => {
    const onOnline = () => {
      reloadFrontVideo();
      // Re-puxa a playlist para aplicar mudanças perdidas enquanto estava offline
      fetchPlaylist().then(d => { if (d) applyPlaylistUpdate(d); }).catch(() => {});
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [reloadFrontVideo, fetchPlaylist, applyPlaylistUpdate]);

  // ── render ─────────────────────────────────────────────────
  if (phase === 'loading')   return <StatusScreen text="Carregando playlist..." />;
  if (phase === 'not_found') return <StatusScreen text="Playlist não encontrada" sub={`Código: ${code}`} action={() => navigate('/tv')} actionLabel="Voltar" />;
  if (phase === 'error')     return <StatusScreen text={errorMsg} sub="Reconectando em 10 segundos..." />;

  const isVertical   = !!(playlistRef.current?.isVertical);
  const singleVideo  = isSingleVideo();

  // CSS de recorte para modo video wall
  const wallStyle: React.CSSProperties | undefined = side ? {
    position: 'absolute',
    width: '200vw',
    height: '100%',
    left: side === 'right' ? '-100vw' : '0',
    top: 0,
  } : undefined;

  const slots = (
    <>
      {/* Slot A */}
      <div style={{ ...styles.slot, opacity: opA, zIndex: frontSlot === 'A' ? 2 : 1, transition: `opacity ${FADE_MS}ms ease-in-out` }}>
        {contentA && (
          <ItemRenderer
            key={contentA.id}
            item={contentA.item}
            isVertical={isVertical}
            onVideoEnd={frontSlot === 'A' && !singleVideo ? advanceItem : undefined}
            videoLoop={frontSlot !== 'A' || singleVideo}
            onReady={frontSlot !== 'A' ? handleBackReady : undefined}
            onVideoRef={setVideoA}
            onMediaError={frontSlot === 'A' ? reloadFrontVideo : undefined}
          />
        )}
      </div>

      {/* Slot B */}
      <div style={{ ...styles.slot, opacity: opB, zIndex: frontSlot === 'B' ? 2 : 1, transition: `opacity ${FADE_MS}ms ease-in-out` }}>
        {contentB && (
          <ItemRenderer
            key={contentB.id}
            item={contentB.item}
            isVertical={isVertical}
            onVideoEnd={frontSlot === 'B' && !singleVideo ? advanceItem : undefined}
            videoLoop={frontSlot !== 'B' || singleVideo}
            onReady={frontSlot !== 'B' ? handleBackReady : undefined}
            onVideoRef={setVideoB}
            onMediaError={frontSlot === 'B' ? reloadFrontVideo : undefined}
          />
        )}
      </div>
    </>
  );

  return (
    <div style={styles.root}>
      {/* Modo normal: slots direto no root */}
      {/* Modo wall: slots em wrapper 200vw deslocado para mostrar só metade */}
      {side
        ? <div style={wallStyle}>{slots}</div>
        : slots
      }
    </div>
  );
};

// ── ItemRenderer ──────────────────────────────────────────────

type ItemProps = {
  item: FullPlaylistItem;
  isVertical: boolean;
  onVideoEnd?: () => void;
  videoLoop: boolean;
  onReady?: () => void;
  onVideoRef?: (el: HTMLVideoElement | null) => void;
  onMediaError?: () => void;
};

const ItemRenderer = ({ item, isVertical, onVideoEnd, videoLoop, onReady, onVideoRef, onMediaError }: ItemProps) => {
  switch (item.type) {
    case 'image':
      return (
        <img
          src={item.media?.url}
          alt={item.media?.desc || ''}
          style={{ width: '100%', height: '100%', objectFit: isVertical ? 'contain' : 'cover', display: 'block' }}
        />
      );

    case 'video':
      return (
        <video
          ref={onVideoRef}
          src={item.media?.url}
          autoPlay
          loop={videoLoop}
          muted
          playsInline
          preload="auto"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onCanPlay={onReady}
          onEnded={onVideoEnd}
          onError={onMediaError}
          onStalled={onMediaError}
        />
      );

    case 'priceTable': {
      const layouts = item.layouts?.length ? item.layouts : (item.priceTable ? [item.priceTable] : []);
      return (
        <div style={{ width: '100%', height: '100%', overflow: 'hidden', background: '#000' }}>
          {layouts.map((l, i) => (
            <div key={i} dangerouslySetInnerHTML={{ __html: l.html }} style={{ width: '100%', height: '100%' }} />
          ))}
        </div>
      );
    }

    default:
      return null;
  }
};

// ── StatusScreen ──────────────────────────────────────────────

const StatusScreen = ({ text, sub, action, actionLabel }: {
  text: string; sub?: string; action?: () => void; actionLabel?: string;
}) => (
  <div style={styles.status}>
    <div style={{ fontSize: 28, fontWeight: 600, color: '#f1f5f9' }}>{text}</div>
    {sub && <div style={{ fontSize: 16, color: '#64748b' }}>{sub}</div>}
    {action && <button style={styles.btn} onClick={action}>{actionLabel}</button>}
  </div>
);

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  root: {
    width: '100vw',
    height: '100vh',
    background: '#000',
    overflow: 'hidden',
    cursor: 'none',
    position: 'relative',
  },
  slot: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
  },
  status: {
    width: '100vw',
    height: '100vh',
    background: '#0a0f1a',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    fontFamily: "'Inter', sans-serif",
  },
  btn: {
    marginTop: 16,
    padding: '12px 32px',
    borderRadius: 10,
    border: 'none',
    background: '#14b8a0',
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default PlayerPage;
